import React, { useRef, useState, useEffect } from 'react';
import { Text, Icon, Link } from 'components/ui';
import Chevron from 'components/Chevron';
import Dropdown from 'components/Dropdown/Dropdown';
import Modal from 'components/Modal/Modal';
import UI from 'utils/UI';
import ReviewsStats from 'components/ProductPage/RatingsAndReviews/ReviewsStats/ReviewsStats';
import localeUtils from 'utils/LanguageLocale';
import { wrapFunctionalComponent } from 'utils/framework';
import ReviewsAnchorEN from 'components/ProductPage/ReviewsAnchor/locales/ReviewsAnchor_en_US.js';
import ReviewsAnchorFR from 'components/ProductPage/ReviewsAnchor/locales/ReviewsAnchor_fr_CA.js';
import {
    modal, mediaQueries, colors, space, buttons, fontSizes, fontWeights, zIndices
} from 'style/config';
import mediaUtils from 'utils/Media';
const { Media } = mediaUtils;

const { getLocalisedDataFromResource } = localeUtils;
const { isSMUI } = UI;

function ReviewsAnchorDropDown({
    reviews,
    formattedReviewCount,
    reviewStatistics,
    initialTotalReviews,
    showProsAndCons,
    currentProduct,
    clickHandler
}) {
    const getText = getLocalisedDataFromResource(ReviewsAnchorEN, ReviewsAnchorFR);
    const dropdownRef = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hoverSuppressed, setHoverSuppressed] = useState(false);

    const seeAllLinkSharedProps = {
        color: colors.link,
        padding: 2,
        marginBottom: -2,
        marginX: -2,
        children: getText('seeAllReviews')
    };

    const renderTriggerPill = () => {
        const openState = isSMUI() ? isModalOpen : isDropdownOpen;

        return (
            <Chevron
                isThicker={false}
                direction={openState ? 'up' : 'down'}
                aria-expanded={openState}
            />
        );
    };

    const handleDropdownTrigger = (e, open) => setIsDropdownOpen(open);

    useEffect(() => {
        const rootEl = dropdownRef.current?.rootRef?.current;

        function handleLeave() {
            if (hoverSuppressed) {
                setHoverSuppressed(false);
            }
        }

        if (rootEl) {
            rootEl.addEventListener('mouseleave', handleLeave);
        }

        return () => {
            if (rootEl) {
                rootEl.removeEventListener('mouseleave', handleLeave);
            }
        };
    }, [hoverSuppressed]);

    const handleChevronClick = e => {
        e.preventDefault();
        e.stopPropagation();

        if (isSMUI()) {
            setIsModalOpen(true);
        } else if (!hoverSuppressed) {
            dropdownRef.current?.triggerDropdown(e, !isDropdownOpen);
        }
    };

    const handleChevronMouseEnter = e => {
        if (isSMUI() || hoverSuppressed) {
            return;
        }

        dropdownRef.current?.triggerDropdown(e, true);
    };

    const handleSeeAllReviewsClick = e => {
        clickHandler(e);
        setHoverSuppressed(true);
        dropdownRef.current?.triggerDropdown(e, false);
    };

    return (
        <React.Fragment>
            <Media greaterThan='xs'>
                {reviews > 0 && (
                    <Dropdown
                        id='reviews_anchor_dropdown'
                        ref={dropdownRef}
                        closeOnClick={false}
                        useClick={false}
                        hasCustomScroll={false}
                        onTrigger={handleDropdownTrigger}
                        hasDelay={false}
                    >
                        <Dropdown.Trigger
                            aria-label={getText('reviewsLabel', [formattedReviewCount, reviews])}
                            css={styles.trigger}
                        >
                            <span
                                onClick={handleChevronClick}
                                onMouseEnter={handleChevronMouseEnter}
                            >
                                {renderTriggerPill()}
                            </span>
                        </Dropdown.Trigger>
                        <Dropdown.Menu
                            offset={2}
                            width={400}
                            padding={modal.paddingSm}
                            left={-140}
                            zIndex={zIndices.dropdown}
                        >
                            <Text
                                is='h2'
                                marginTop={`${space[2]}px`}
                                fontSize={[fontSizes.md, fontSizes.lg]}
                                fontWeight={fontWeights.bold}
                                data-at='ratings_reviews_section'
                                children={getText('ratingsReviews')}
                            />
                            <button
                                type='button'
                                aria-label={getText('closeButton')}
                                css={styles.close}
                                onClick={e => {
                                    setHoverSuppressed(true);
                                    dropdownRef.current?.triggerDropdown(e, false);
                                }}
                            >
                                <Icon
                                    size={modal.xSize}
                                    name='x'
                                />
                            </button>
                            <ReviewsStats
                                sentiments={currentProduct?.sentiments}
                                reviewStatistics={reviewStatistics}
                                totalReviewCount={initialTotalReviews}
                                showProsAndCons={showProsAndCons}
                                isDropDownView={true}
                            />
                            <Link
                                onClick={handleSeeAllReviewsClick}
                                {...seeAllLinkSharedProps}
                            />
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </Media>
            <Media at='xs'>
                {reviews > 0 && (
                    <span
                        onClick={handleChevronClick}
                        css={styles.chevronTriggerSm}
                    >
                        {renderTriggerPill()}
                    </span>
                )}
                {isModalOpen && (
                    <Modal
                        isOpen={true}
                        isDrawer={true}
                        onDismiss={() => setIsModalOpen(false)}
                    >
                        <Modal.Header>
                            <Modal.Title children={getText('ratingsReviews')} />
                        </Modal.Header>
                        <Modal.Body
                            marginLeft={space[3]}
                            paddingBottom={[modal.paddingSm]}
                            paddingX={null}
                            paddingTop={0}
                        >
                            <ReviewsStats
                                sentiments={currentProduct?.sentiments}
                                reviewStatistics={reviewStatistics}
                                totalReviewCount={initialTotalReviews}
                                showProsAndCons={showProsAndCons}
                                isDropDownView={true}
                            />
                            <Link
                                onClick={e => {
                                    setIsModalOpen(false);
                                    clickHandler(e);
                                }}
                                {...seeAllLinkSharedProps}
                            />
                        </Modal.Body>
                    </Modal>
                )}
            </Media>
        </React.Fragment>
    );
}

const styles = {
    trigger: {
        outline: 0,
        marginLeft: space[1]
    },
    close: {
        position: 'absolute',
        top: space[5],
        right: 0,
        zIndex: 3,
        lineHeight: 0,
        height: `${buttons.HEIGHT_XS}px`,
        paddingLeft: modal.paddingX[0],
        paddingRight: modal.paddingX[0],
        [mediaQueries.sm]: {
            paddingLeft: modal.paddingX[1],
            paddingRight: modal.paddingX[1]
        }
    },
    chevronTriggerSm: {
        display: 'inline-block',
        marginLeft: `${space[1]}px`,
        cursor: 'pointer',
        verticalAlign: 'middle'
    }
};

export default wrapFunctionalComponent(ReviewsAnchorDropDown, 'ReviewsAnchorDropDown');
